/**
 * Client-side identity hashing (design §4.5; hash-parity-resolution.md).
 *
 * The pixel hashes EMAIL ONLY: inner SHA-256 of the normalized address. The
 * collector pepper-wraps it into the canonical, tenant-isolated identity hash.
 * Raw email NEVER leaves the browser. PHONE is NOT hashed client-side — correct
 * E.164 needs libphonenumber (~200KB, blows the ≤25KB budget) and any divergence
 * silently breaks matching; phone identity rides `visitor_uuid` via the server.
 *
 * `normalizeEmail` is BYTE-IDENTICAL to lib/attribution/security/normalize.ts —
 * if that contract changes, change it here too, or the browser↔server join fails.
 */
import type { IdentityPayload, IdentityTraits } from './types';
import { sha256hex } from './util';

export async function buildIdentity(traits: IdentityTraits): Promise<IdentityPayload> {
  const out: IdentityPayload = {};
  if (traits.externalLeadId) out.externalLeadId = String(traits.externalLeadId);

  // Email only. Phone is intentionally ignored on the client.
  if (traits.email) {
    const norm = normalizeEmail(traits.email);
    if (norm) {
      const h = await sha256hex(norm);
      if (h) out.emailHash = h; // inner SHA-256; collector pepper-wraps
    }
    // Fail-closed: invalid/unhashable email → no emailHash, never raw.
  }
  return out;
}

/**
 * BYTE-IDENTICAL to lib/attribution/security/normalize.ts → normalizeEmail().normalized.
 * NFKC → trim → lowercase; reject unless exactly one '@', non-empty local, dotted domain.
 * No Gmail dot/+tag stripping (matches Meta/Google specs; avoids false merges).
 */
export function normalizeEmail(raw: string | null | undefined): string | null {
  if (typeof raw !== 'string') return null;
  const normalized = raw.normalize('NFKC').trim().toLowerCase();
  if (normalized.length === 0 || normalized.length > 320) return null;

  const at = normalized.indexOf('@');
  if (at <= 0 || at !== normalized.lastIndexOf('@')) return null;

  const local = normalized.slice(0, at);
  const domain = normalized.slice(at + 1);
  if (local.length === 0 || domain.length === 0) return null;
  if (/\s/.test(normalized)) return null;
  if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(domain)) return null;

  return normalized;
}
